import React, { useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { validateManualTime } from './utils';
import DATA_TEST_IDS from 'chameleon-src/components/assign/AssignManageForm/utils/dataTestIds';

/**
 * Allow manual time selection for Assign/Manage modals.
 * https://academicmerit.atlassian.net/browse/FY-19241
 * Since we're using a custom React DatePicker input, we're handling
 * the manual time selection with an editable HTML element (span or div)
 * */
const ManualTimeSelection = (props) => {
  const { className, value, setManualTime, disabled } = props;
  const elementRef = useRef(null);
  const elementValue = useRef(value);

  useEffect(() => {
    elementValue.current = value;
  }, [value]);

  const onKeyDown = (event) => {
    const { keyCode } = event;

    // On ENTER or ESCAPE key. Event propagation should be stopped. Validation time is done onBlur.
    if ([13, 27].includes(keyCode)) {
      event.preventDefault();
      event.stopPropagation();

      elementRef.current.blur();
    }
  };

  const resetTimeContent = () => {
    elementRef.current.textContent = elementValue.current;
  };

  const onBlur = (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (value !== elementRef.current.textContent) {
      validateManualTime(
        elementRef.current.textContent,
        setManualTime,
        resetTimeContent,
      );
    }
  };

  return (
    <span
      role="button"
      tabIndex={disabled ? '-1' : '0'}
      ref={elementRef}
      contentEditable={!disabled}
      suppressContentEditableWarning
      onKeyDown={onKeyDown}
      onBlur={onBlur}
      data-test-id={DATA_TEST_IDS.DATE_HOURS_MINUTES_LABEL}
      className={className}
    >
      {value}
    </span>
  );
};

ManualTimeSelection.displayName = 'ManualTimeSelection';

ManualTimeSelection.propTypes = {
  // The value to be displayed in the element.
  value: PropTypes.string.isRequired,
  // Callback to set the time based on the changes from this component.
  setManualTime: PropTypes.func.isRequired,
};

export default ManualTimeSelection;
